// The relay's JSON API (PROTOCOL.md §4) with access-token refresh. Browsers keep the refresh token in the relay's
// HttpOnly cookie; native clients (the CLI, and the same shape as the Apple apps) hold it and pass it back.

export interface Tokens {
  accountId: string;
  deviceId: string;
  accessToken: string;
  accessExpiresAt: number;
  refreshToken?: string;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    /** The error reply's other fields (a quota's `limit` and `used`, PROTOCOL.md §9.3). */
    readonly details: Record<string, unknown> = {},
  ) {
    super(message);
  }
}

export interface ApiOptions {
  origin: string;
  /** 'native' keeps the refresh token in `tokens` (and calls `onTokens` to persist it); 'browser' uses the cookie. */
  client: 'browser' | 'native';
  tokens?: Tokens | null;
  onTokens?: (tokens: Tokens | null) => void;
  /** Asks for a passkey check when the relay wants a fresh one (`403 reauth`, PROTOCOL.md §3.4); the call goes again. */
  reauthenticate?: () => Promise<void>;
}

export class Api {
  readonly origin: string;
  readonly client: 'browser' | 'native';
  tokens: Tokens | null;
  /** See `ApiOptions.reauthenticate`; without it a `reauth` refusal is thrown like any other. */
  reauthenticate: (() => Promise<void>) | null;
  private readonly onTokens: (tokens: Tokens | null) => void;
  private refreshing: Promise<Tokens> | null = null;

  constructor(o: ApiOptions) {
    this.origin = o.origin.replace(/\/$/, '');
    this.client = o.client;
    this.tokens = o.tokens ?? null;
    this.onTokens = o.onTokens ?? (() => {});
    this.reauthenticate = o.reauthenticate ?? null;
  }

  /** Records tokens from a sign-in, sign-up, pairing or refresh reply. */
  accept(reply: Tokens): Tokens {
    const refreshToken = reply.refreshToken ?? this.tokens?.refreshToken;
    this.tokens = { accountId: reply.accountId, deviceId: reply.deviceId, accessToken: reply.accessToken, accessExpiresAt: reply.accessExpiresAt, refreshToken };
    this.onTokens(this.tokens);
    return this.tokens;
  }

  forget() {
    this.tokens = null;
    this.onTokens(null);
  }

  /** A fresh access token (refreshing a minute before expiry; one refresh at a time). */
  async access(): Promise<string> {
    if (this.tokens && this.tokens.accessExpiresAt - Date.now() > 60_000) return this.tokens.accessToken;
    return (await this.refresh()).accessToken;
  }

  refresh(): Promise<Tokens> {
    this.refreshing ??= this.call<Tokens>('POST', '/api/auth/refresh', { client: this.client, refreshToken: this.tokens?.refreshToken }, false)
      .then((t) => this.accept(t))
      .catch((e) => {
        if (e instanceof ApiError && e.status === 401) this.forget();
        throw e;
      })
      .finally(() => (this.refreshing = null));
    return this.refreshing;
  }

  /** An API call; `auth` adds the access token and retries once after a refresh on an expired token. */
  async call<T>(method: string, path: string, body?: unknown, auth = true, headers: Record<string, string> = {}): Promise<T> {
    const send = async () => {
      const h: Record<string, string> = { ...headers };
      if (body !== undefined) h['content-type'] = 'application/json';
      if (auth) h.authorization = `Bearer ${await this.access()}`;
      return fetch(this.origin + path, { method, headers: h, body: body === undefined ? undefined : JSON.stringify(body), credentials: 'same-origin' });
    };
    let res = await send();
    if (auth && res.status === 401 && this.tokens) {
      const err = (await res.clone().json().catch(() => ({}))) as { error?: string };
      if (err.error === 'expired') {
        await this.refresh();
        res = await send();
      }
    }
    if (auth && res.status === 403 && this.reauthenticate) {
      const err = (await res.clone().json().catch(() => ({}))) as { error?: string };
      if (err.error === 'reauth') {
        await this.reauthenticate();
        res = await send();
      }
    }
    const text = await res.text();
    const data = text ? JSON.parse(text) : {};
    if (!res.ok) throw new ApiError(res.status, data.error ?? 'http', data.message ?? `${method} ${path}: ${res.status}`, data);
    return data as T;
  }

  get<T>(path: string) {
    return this.call<T>('GET', path);
  }
}
